import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TOKEN_KEY = "adcraft_token";

export { BACKEND_URL };

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Auth travels exclusively via httpOnly cookies (sent automatically by
// withCredentials). We intentionally do NOT read/write the token in
// localStorage — that would expose it to XSS and defeat the HttpOnly design.

// --- Silent token refresh -------------------------------------------------
// On a 401 from a normal API call, transparently refresh the access token
// (using the httpOnly refresh cookie) exactly once and retry the request.
// Concurrent 401s are queued so refresh runs a single time. Only a failed
// refresh actually logs the user out.
let isRefreshing = false;
let refreshQueue = [];

const flushQueue = (err) => {
  refreshQueue.forEach(({ resolve, reject }) => (err ? reject(err) : resolve()));
  refreshQueue = [];
};

const isAuthEndpoint = (url = "") =>
  ["/auth/login", "/auth/register", "/auth/refresh", "/auth/me"].some((p) => url.includes(p));

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { response, config } = error;
    if (!response || !config || response.status !== 401 || config._retry || isAuthEndpoint(config.url || "")) {
      return Promise.reject(error);
    }
    config._retry = true;
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve: () => resolve(api(config)), reject });
      });
    }
    isRefreshing = true;
    try {
      await api.post("/auth/refresh");
      flushQueue(null);
      return api(config);
    } catch (refreshErr) {
      flushQueue(refreshErr);
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("nox-auth-logout"));
      }
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  }
);

// Kept as no-ops for backwards compatibility: auth is cookie-only now.
export function setToken() {}
export function clearToken() {}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
